import { useJobillaOrgId } from '@/hooks'
import { useAuth } from '@/lib'
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid'
import { useCustomerValidateBillingDetails } from '@jobilla/nexus-sdk'
import { AnimatePresence, motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { SubtleLoader } from '../../ui'
import { DisplayAddress } from './DisplayAddress'
import { DisplayPaymentDetails } from './DisplayPaymentDetails'
import { DisplayTaxDetails } from './DisplayTaxDetails'

/**
 * Shows a summary of the users current details or error containing what might be wrong.
 * @constructor
 */
export function BillingSummary() {
	const { t } = useTranslation('common')
	const { permissions } = useAuth()
	const organisationId = useJobillaOrgId()
	const { data: billingData, isSuccess, isLoading } = useCustomerValidateBillingDetails(organisationId, undefined, {
		enabled: !!organisationId && !!permissions?.includes('billing:read'),
	})

	if (isLoading) {
		return <SubtleLoader delay={0} />
	}

	if (!isSuccess) {
		return <p>{t('billing.summary.failedToLoad')}</p>
	}

	return (
		<AnimatePresence mode="wait">
			{!billingData?.validation.valid
				? (
					<motion.div
						key="error"
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -10 }}
						transition={{ duration: 0.3, ease: 'easeOut' }}
						className="bg-red-50 border border-red-200 rounded-lg p-5"
					>
						<div className="flex items-start gap-3">
							<ExclamationCircleIcon className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
							<div className="flex-1 space-y-4">
								<div>
									<div className="font-semibold text-red-900 mb-2">
										{billingData?.validation.message || t('billing.summary.incomplete')}
									</div>
									<div className="text-sm text-red-800 leading-relaxed">
										{t('billing.summary.updateDetails')}
									</div>
								</div>

								{billingData && billingData?.validation.missingFields
										&& billingData.validation.missingFields.length > 0
									? (
										<div className="bg-white/50 rounded-md p-3 border border-red-100">
											<div className="text-xs font-medium text-red-900 uppercase tracking-wide mb-2">
												{t('billing.summary.missingFields')}
											</div>
											<ul className="space-y-1.5">
												{billingData.validation.missingFields.map(
													(missingField, index) => {
														return (
															<li
																key={index}
																className="flex items-start gap-2 text-sm text-red-800"
															>
																<span className="text-red-400 mt-0.5">•</span>
																<span className="capitalize">{missingField}</span>
															</li>
														)
													},
												)}
											</ul>
										</div>
									)
									: null}
							</div>
						</div>
					</motion.div>
				)
				: (
					<motion.div
						key="success"
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -10 }}
						transition={{ duration: 0.3, ease: 'easeOut' }}
						className="bg-green-50 border border-green-200 rounded-lg p-4"
					>
						<div className="flex items-start gap-3">
							<CheckCircleIcon className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
							<div className="flex-1 space-y-3">
								<div>
									<div className="font-medium text-green-900 mb-1">
										{t('billing.summary.complete')}
									</div>
									<div className="text-sm text-green-700">
										{t('billing.summary.ready')}
									</div>
								</div>

								<div className="space-y-2 text-sm">
									<div>
										<span className="font-medium text-gray-700">{t('billing.summary.name')}</span>
										<span className="ml-2 text-gray-600">{billingData?.customer?.name}</span>
									</div>
									<div>
										<span className="font-medium text-gray-700">{t('billing.summary.email')}</span>
										<span className="ml-2 text-gray-600">{billingData?.customer?.email}</span>
									</div>
									<div>
										<span className="font-medium text-gray-700">
											{t('billing.summary.address')}
										</span>
										{billingData?.customer?.address
											? (
												<div className="ml-2 text-gray-600">
													<DisplayAddress address={billingData?.customer?.address} />
												</div>
											)
											: null}
									</div>
									<div>
										<span className="font-medium text-gray-700">
											{t('billing.summary.payment')}
										</span>
										{billingData?.customer?.paymentOverview
											? (
												<div className="ml-2 text-gray-600">
													<DisplayPaymentDetails
														overview={billingData?.customer?.paymentOverview}
														tax={billingData?.customer?.tax}
													/>
												</div>
											)
											: null}
									</div>
									<div>
										<span className="font-medium text-gray-700">{t('billing.summary.vat')}</span>
										<span className="ml-2 text-gray-600">
											<DisplayTaxDetails tax={billingData.customer?.tax} />
										</span>
									</div>
								</div>
							</div>
						</div>
					</motion.div>
				)}
		</AnimatePresence>
	)
}
